import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { SplitterModule } from 'primeng/splitter';
import { Toast } from 'primeng/toast';
import { selectIsLoading, selectPredictionErrorMessage, selectPredictions } from '../../../../store/selectors/prediction.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectSports } from '../../../../store/selectors/sport.selectors';
import { selectCompetitions } from '../../../../store/selectors/competitions.selectors';
import { followPrediction, loadFollowedPredictions, unfollowPrediction } from '../../../../store/actions/follow-prediction.actions';
import { selectFollowedPredictionErrorMessage, selectFollowedPredictions, selectIsFollowedPredictionLoading } from '../../../../store/selectors/follow-prediction.selectors';
import { SkeletonModule } from 'primeng/skeleton';
import { DatePickerModule } from 'primeng/datepicker';
import { PredictionService } from '../../../../services/prediction.service';
import { CreditsService } from '../../../../services/credits.service';
import { loadUserCredits } from '../../../../store/actions/credits.actions';

@Component({
  selector: 'app-probabilities',
  imports: [SplitterModule, ReactiveFormsModule, DialogModule, DatePickerModule, Toast, SkeletonModule, DialogModule, MultiSelectModule, CommonModule, SelectModule, RadioButtonModule, InputNumberModule, DropdownModule, DividerModule, ButtonModule, InputTextModule, FormsModule
  ],
  templateUrl: './probabilities.component.html',
  providers: [MessageService],
  standalone: true,
  styleUrl: './probabilities.component.scss'
})
export class ProbabilitiesComponent implements OnInit {
  constructor(private router: Router,
    private messageService: MessageService,
    private predictionService: PredictionService,
    private fb: FormBuilder,
    private creditsService: CreditsService
  ) {
  }
  private store = inject(Store);
  userCredits = signal<number>(0);
  isSubmittingPrediction = signal<boolean>(false);
  purchasePredictionDialog: boolean = false;
  predictionForm!: FormGroup
  minDate = new Date();
  priceUnitPrediction = 1
  showConfirmDialog = false;
  isMobile = window.innerWidth < 768;

  loadingPredictionId = signal<string | null>(null);
  listOfPredictions = toSignal(this.store.select(selectPredictions));
  loading = toSignal(this.store.select(selectIsLoading));
  error = toSignal(this.store.select(selectPredictionErrorMessage));
  sports = toSignal(this.store.select(selectSports));
  competitions = toSignal(this.store.select(selectCompetitions))

  folowedPredictions = toSignal(this.store.select(selectFollowedPredictions));
  isLoadingFollowedPrediction = toSignal(this.store.select(selectIsFollowedPredictionLoading))
  errorFolowedPrediction = toSignal(this.store.select(selectFollowedPredictionErrorMessage));
  selectedSport = signal(null)
  selectedCompetition = signal<string[]>([])


  sportOptionsForForm = signal([
    { label: 'Football', value: 'football' },
    { label: 'Basketball', value: 'basketball' },
    { label: 'Tennis', value: 'tennis'},
    { label: 'Rugby', value: 'rugby' }
  ]);

  ngOnInit(): void {
    this.predictionForm = this.fb.group({
      team_1: ['', Validators.required, Validators.minLength(2)],
      team_2: ['', Validators.required, Validators.minLength(2)],
      sport: [null, Validators.required],
      date: [null, Validators.required],
      event: [null, Validators.required]
    })
    this.loadUserCredits();
  }

  sortedPredictions = computed(() => {
    return this.predictions().sort((a, b) => {
      const dateA = new Date(a.event_date);
      const dateB = new Date(b.event_date);
      return  dateA.getTime() - dateB.getTime();
    });
  });




  loadUserCredits() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.user_id) {
      this.creditsService.getUserCredits(user.user_id).subscribe({
        next: (response) => {
          this.userCredits.set(response.balance || 0);
        },
        error: (error) => {
          console.error('Error loading credits:', error);
          this.userCredits.set(0);
        }
      });
    }
  }
  goToSubscription() {
    this.router.navigate(['/profil']);
  }
  competitionsOptions = computed(() => {
    return this.competitions()?.map(competition => ({
      label: competition["name"],
      code: competition["id"]
    })) || [];
  });
  sportOptions = computed(() => {
    const options = this.sports()?.map(sport => ({
      label: sport["sport_name"],
      code: sport["id"]
    })) || [];
    return [{ label: "Tous", code: "all" }, ...options];

  })

  unitSportOptions = computed(() => {
    return this.sportOptions().map(sport => ({
      label: sport.label,
      value: sport.code
    }));
  })

  confirmPurchase() {
    if (this.predictionForm.invalid) {
      this.markFormGroupTouched();
      return;
    }
    this.showConfirmDialog = true;
  }

  processPurchase() {
    this.showConfirmDialog = false;
    this.onSubmitPrediction();
  }

  getSelectedSportName(): string {
    const selectedSport = this.unitSportOptions().find(sport => sport.value === this.predictionForm.get('sport')?.value);
    return selectedSport ? selectedSport.label : '';
  }


  onSubmitPrediction() {
    if (this.predictionForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    if (this.userCredits() < this.priceUnitPrediction) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Crédit insuffisant',
        detail: `Vous avez besoin de ${this.priceUnitPrediction} jetons pour acheter une prédiction`,
        life: 5000
      });
      return;
    }

    this.isSubmittingPrediction.set(true);
    const formData = this.predictionForm.value;
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    const selectedSport = this.unitSportOptions().find(sport => sport.value === formData.sport);
    const sportName = selectedSport ? selectedSport.label : '';

    const requestData = {
      user_id: user.user_id,
      event_date: formData.date.toISOString(), 
      team_home_name: formData.team_1,
      team_away_name: formData.team_2,
      sport_name: sportName,
      competition_name: formData.competition || '' 
    };

    this.predictionService.requestCustomPrediction(requestData).subscribe({
      next: (response) => {
        this.isSubmittingPrediction.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Demande envoyée !',
          detail: 'Votre prédiction personnalisée sera traitée sous peu.',
          life: 8000
        });

        this.userCredits.set(this.userCredits() - this.priceUnitPrediction);
        this.store.dispatch(loadUserCredits({ userId: user.user_id }));

        this.predictionForm.reset();


      },
      error: (error) => {
        this.isSubmittingPrediction.set(false);
        console.error('Error requesting prediction:', error);

        let errorMessage = 'Impossible de traiter votre demande.';
        if (error.error?.error) {
          switch (error.error.error) {
            case 'Insufficient credits':
              errorMessage = 'Crédits insuffisants pour cette transaction.';
              break;
            case 'Invalid user ID':
              errorMessage = 'Utilisateur invalide. Veuillez vous reconnecter.';
              break;
            case 'User does not have a credit balance':
              errorMessage = 'Aucun compte de crédit trouvé. Contactez le support.';
              break;
            default:
              errorMessage = error.error.error;
          }
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: errorMessage,
          life: 6000
        });
      }
    });
  }


  private markFormGroupTouched() {
    Object.keys(this.predictionForm.controls).forEach(key => {
      this.predictionForm.get(key)?.markAsTouched();
    });
  }
  goToPayment() {
    this.router.navigate(['/profil']);
  }
  onSportChange(event: any) {
    this.selectedCompetition.set([])
    this.selectedSport.set(event.value)
  }
  onCompetitionChange(event: any) {
    this.selectedSport.set(null)
    this.selectedCompetition.set(event.value)
  }

  isFollowed(predictionId: string): boolean {
    return this.folowedPredictions()?.some(prediction => prediction.prediction_id === predictionId) || false;
  }
  predictions = computed(() => {
    let predict = []
    if ((this.selectedSport() == null || this.selectedSport() == 'Tous') && this.selectedCompetition().length === 0) {
      predict = this.listOfPredictions() || []
    } else if (this.selectedSport() && this.listOfPredictions()) {
      predict = this.listOfPredictions()?.filter(prediction => prediction.sport === this.selectedSport()) || []
    } else if (this.selectedCompetition()) {
      predict = this.listOfPredictions()?.filter(prediction => this.selectedCompetition().includes(prediction.competition)) || []
    }
    return predict?.map(prediction => ({
      ...prediction,
      isFollowed: this.isFollowed(prediction.id),
    })) || [];
  }
  );
  toggleFollow(prediction: any) {
    const isPredictionFolllowed = this.isFollowed(prediction.id);
    const userId = JSON.parse(localStorage.getItem('user') || '{}').user_id
    this.loadingPredictionId.set(prediction.id);
    if (!isPredictionFolllowed) {
      this.store.dispatch(followPrediction({ predictionId: prediction.id, userId: JSON.parse(localStorage.getItem('user') || '{}').user_id.toString() }));
    } else {
      this.store.dispatch(unfollowPrediction({ predictionId: prediction.id, userId: userId.toString() }));
    }
  }
  goToDetails(predictionId: string) {
    this.router.navigate(['/details', predictionId]);
  }

  changeLoading = effect(() => {
    if (!this.isLoadingFollowedPrediction()) {
      this.loadingPredictionId.set(null);
    }
  })

  errorFollow = effect(() => {
    if (this.errorFolowedPrediction()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.errorFolowedPrediction() || 'An error occurred while following the prediction.',
        life: 3000
      });
    }
  })

  onSubscribe() {
    this.router.navigate(['/profil'])
  }

  openPurchasePredictionDialog() {
    this.purchasePredictionDialog = true;
  }

  onSubmit() {
    if (this.predictionForm.invalid) {
      this.predictionForm.markAllAsTouched();
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Prédiction envoyée avec succès',
      life: 3000
    });



    this.predictionForm.reset();
    this.purchasePredictionDialog = false;
  }
}
